import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import socket from "../../components/ScoreboardAdmin/socketClient";
import { getCompetitionById } from "../../services/competitionsServices";
import {
  createEmptyJJScore,
  calculateJJCombatResult,
} from "../../utils/jjCombatScoring";
import JJCombatPublicRow from "./components/JJCombatPublicRow";
import {
  DISCIPLINES,
  buildAgeMappings,
  getSeasonStartYear,
  isFemale,
  normalizeDiscipline,
  weightCategoriesByDiscipline,
} from "../../utils/categoryRules";

const PART_CONFIG = {
  1: {
    label: "Partie 1",
    description: "Atémis pieds-poings",
  },
  2: {
    label: "Partie 2",
    description: "Projections / Amenées au sol, Clés & étranglements debout",
  },
  3: {
    label: "Partie 3",
    description: "Immobilisations, Clés & étranglements au sol",
  },
};

const safeNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const formatChrono = (seconds) => {
  const total = Math.max(0, safeNumber(seconds, 0));
  const mins = String(Math.floor(total / 60)).padStart(2, "0");
  const secs = String(total % 60).padStart(2, "0");
  return `${mins}:${secs}`;
};

const normalizeLooseLabel = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const getCompetiteurDisplayName = (competiteur) => {
  if (!competiteur) return "Combattant";

  const nom = String(
    competiteur.nom_competiteur || competiteur.nom || "",
  ).trim();
  const prenom = String(
    competiteur.prenom_competiteur || competiteur.prenom || "",
  ).trim();

  const full = `${nom.toUpperCase()} ${prenom}`.trim();
  return full || "Combattant";
};

const getFightCompetitor = (fight, side = 1) => {
  if (!fight) return null;

  if (side === 1) {
    return (
      fight.c1 ||
      fight.competiteur1 ||
      fight.red ||
      fight.redCompetiteur ||
      null
    );
  }

  return (
    fight.c2 ||
    fight.competiteur2 ||
    fight.blue ||
    fight.blueCompetiteur ||
    null
  );
};

const getAnyCompetitor = (data) =>
  getFightCompetitor(data, 1) ||
  getFightCompetitor(data, 2) ||
  data?.competiteur1 ||
  data?.competiteur2 ||
  data?.redCompetiteur ||
  data?.blueCompetiteur ||
  null;

const getFightGenderLabel = (fight) => {
  const direct = String(fight?.genre || "").trim().toUpperCase();
  if (direct === "F") return "Féminin";
  if (direct === "M") return "Masculin";

  const candidate = getAnyCompetitor(fight);
  const gender = String(
    candidate?.genre_competiteur || candidate?.genre || "",
  ).trim().toUpperCase();

  if (gender === "F") return "Féminin";
  if (gender === "M") return "Masculin";

  return fight?.genre || "";
};

const getDisciplineKey = (discipline) => {
  const raw = normalizeLooseLabel(discipline);

  if (
    raw.includes("ju-jitsu") ||
    raw.includes("ju jitsu") ||
    raw.includes("jujitsu") ||
    raw.includes("jj combat") ||
    raw.includes("fighting") ||
    raw.includes("combat")
  ) {
    return DISCIPLINES.JUJITSU;
  }

  if (
    raw.includes("ne-waza") ||
    raw.includes("ne waza") ||
    raw.includes("newaza")
  ) {
    return DISCIPLINES.NEWAZA;
  }

  return normalizeDiscipline(discipline || "Judo");
};

const extractBirthYear = (competiteur) => {
  const raw = String(
    competiteur?.ddn_competiteur ||
      competiteur?.ddn ||
      competiteur?.date_naissance ||
      competiteur?.birthdate ||
      "",
  ).trim();

  if (!raw) return 0;

  const yearMatch = raw.match(/\b(19|20)\d{2}\b/);
  if (yearMatch) return Number(yearMatch[0]);

  const date = new Date(raw);
  const year = date.getFullYear();
  return Number.isFinite(year) ? year : 0;
};

const parseWeightValue = (value) => {
  const raw = String(value ?? "").replace(",", ".").trim();
  if (!raw) return 0;

  const match = raw.match(/\d+(\.\d+)?/);
  if (!match) return 0;

  const n = Number(match[0]);
  return Number.isFinite(n) ? n : 0;
};

const formatWeightBoundary = (value) => {
  const str = Number.isInteger(value) ? String(value) : String(value).replace(".", ",");
  return `${str} kg`;
};

const getAgeCategoryFromRules = (data, disciplineKey) => {
  const candidate = getAnyCompetitor(data);
  const birthYear = extractBirthYear(candidate);
  if (!birthYear) return "";

  const mappings =
    buildAgeMappings(getSeasonStartYear())[disciplineKey]?.France || [];

  const match = mappings.find(({ range = [] }) => {
    const [minYear, maxYear] = range;
    return birthYear >= minYear && birthYear <= maxYear;
  });

  return match?.name || "";
};

const getAgeLabelToShow = (data) => {
  const disciplineKey = getDisciplineKey(data?.discipline);
  const derived = getAgeCategoryFromRules(data, disciplineKey);
  if (derived) return derived;

  return String(
    data?.categorieAge || data?.categorie_age || data?.ageCategory || "",
  ).trim();
};

const getWeightCategoryFromRules = (data, ageLabel) => {
  const disciplineKey = getDisciplineKey(data?.discipline);
  const candidate = getAnyCompetitor(data);

  const weight = parseWeightValue(
    candidate?.poids_competiteur || candidate?.poids || data?.poids || "",
  );

  if (!weight || !ageLabel) return "";

  const gender =
    candidate?.genre_competiteur || candidate?.genre || data?.genre || "";
  const suffix = isFemale(gender) ? "F" : "";

  const buckets =
    weightCategoriesByDiscipline?.[disciplineKey]?.France || null;
  if (!buckets) return "";

  const limits = buckets[`${ageLabel}${suffix}`] || buckets[ageLabel] || null;
  if (!Array.isArray(limits) || !limits.length) return "";

  for (const limit of limits) {
    if (weight <= limit) {
      return `-${formatWeightBoundary(limit)}`;
    }
  }

  return `+${formatWeightBoundary(limits[limits.length - 1])}`;
};

const getWeightLabelToShow = (data, ageLabel) => {
  const derived = getWeightCategoryFromRules(data, ageLabel);
  if (derived) return derived;

  return String(
    data?.categoriePoids || data?.categorie_poids || data?.weightCategory || "",
  ).trim();
};

const buildDisplayMeta = (data) => {
  const ageLabel = getAgeLabelToShow(data);
  const weightLabel = getWeightLabelToShow(data, ageLabel);

  return {
    ageLabel,
    weightLabel,
  };
};

const normalizeJJPayload = (payload) => {
  const source = payload?.data ?? payload ?? {};
  const empty = createEmptyJJScore();

  return {
    discipline: source.discipline || "Ju-Jitsu Combat",

    competiteur1:
      source.competiteur1 || source.redCompetiteur || source.red || null,
    competiteur2:
      source.competiteur2 || source.blueCompetiteur || source.blue || null,

    redScore: { ...empty, ...(source.redScore || source.score1 || {}) },
    blueScore: { ...empty, ...(source.blueScore || source.score2 || {}) },

    currentPart: Math.min(
      3,
      Math.max(
        1,
        safeNumber(source.currentPart || source.partieActive || 1, 1),
      ),
    ),

    chrono: Math.max(
      0,
      safeNumber(source.chrono ?? source.timeLeft ?? source.remainingTime, 0),
    ),

    chronoMax: Math.max(
      1,
      safeNumber(
        source.chronoMax ?? source.dureeCombat ?? source.duration,
        180,
      ),
    ),

    osaeFighter:
      source.osaeFighter ||
      source.immobilisationColor ||
      source.osaeColor ||
      null,

    osaeSeconds: Math.max(
      0,
      safeNumber(
        source.osaeSeconds ?? source.immobilisationSeconds ?? source.osaeTimer,
        0,
      ),
    ),

    osaeRunning: Boolean(source.osaeRunning),

    categorieAge:
      source.categorieAge || source.categorie_age || source.ageCategory || "",

    categoriePoids:
      source.categoriePoids ||
      source.categorie_poids ||
      source.weightCategory ||
      "",

    combatLabel:
      source.combatLabel ||
      source.phaseLabel ||
      source.phase ||
      source.roundLabel ||
      source.tableauLabel ||
      source.tourLabel ||
      "",

    winnerColor: source.winnerColor || null,
    needExtraPeriod: Boolean(source.needExtraPeriod),
    message: source.message || "",
  };
};

function WaitingScreen({
  competitionName,
  tapisId,
  title,
  subtitle,
  nextFight,
}) {
  const nextRed = getFightCompetitor(nextFight, 1);
  const nextBlue = getFightCompetitor(nextFight, 2);
  const nextMeta = buildDisplayMeta(nextFight || {});

  return (
    <div className="w-screen h-screen bg-black text-white flex flex-col items-center justify-center px-8 text-center">
      <div className="text-[clamp(2.2rem,4.4vw,4.6rem)] font-black">
        {competitionName || "Compétition"}
      </div>

      <div className="mt-4 text-[clamp(5.4rem,14vw,12.5rem)] font-black text-emerald-400 leading-none">
        TAPIS {tapisId}
      </div>

      <div className="mt-8 text-[clamp(2rem,3.8vw,4.2rem)] font-bold">
        {title}
      </div>

      {!!subtitle && (
        <div className="mt-3 text-[clamp(1.1rem,2vw,1.8rem)] text-white/70">
          {subtitle}
        </div>
      )}

      {nextFight && (
        <div className="mt-10 rounded-3xl border border-white/15 bg-white/10 px-8 py-6 backdrop-blur max-w-6xl">
          <div className="text-[clamp(1.1rem,1.9vw,1.6rem)] font-black tracking-[0.25em] text-yellow-300">
            PROCHAIN COMBAT
          </div>

          <div className="mt-4 text-[clamp(1.8rem,3.2vw,3.4rem)] font-black">
            {getCompetiteurDisplayName(nextRed)}
            <span className="text-white/50 mx-4">vs</span>
            {getCompetiteurDisplayName(nextBlue)}
          </div>

          <div className="mt-3 text-[clamp(1.1rem,1.9vw,1.6rem)] text-white/80 font-semibold">
            {[
              getFightGenderLabel(nextFight),
              nextMeta.ageLabel,
              nextMeta.weightLabel,
            ]
              .filter(Boolean)
              .join(" • ")}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ScoreboardJJCombatPublic() {
  const { competitionId, tapisId } = useParams();

  const [competitionName, setCompetitionName] = useState("");
  const [combatData, setCombatData] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [nextFight, setNextFight] = useState(null);
  const [nextMsg, setNextMsg] = useState("");

  useEffect(() => {
    const fetchCompetition = async () => {
      try {
        const data = await getCompetitionById(competitionId);
        setCompetitionName(data?.nom_competition || "");
      } catch (error) {
        console.error("Erreur chargement compétition :", error);
      }
    };

    fetchCompetition();
  }, [competitionId]);

  useEffect(() => {
    const roomKey = `${competitionId}_${tapisId}`;
    socket.emit("join_combat", roomKey);

    const onScoreUpdate = (payload) => {
      setCombatData(normalizeJJPayload(payload));
    };

    const onPause = ({ paused }) => {
      setIsPaused(!!paused);
    };

    const onNextFight = ({ nextFight, message }) => {
      setNextFight(nextFight || null);
      setNextMsg(String(message || ""));
    };

    socket.on("score_update", onScoreUpdate);
    socket.on("public_pause", onPause);
    socket.on("public_next_fight", onNextFight);

    return () => {
      socket.off("score_update", onScoreUpdate);
      socket.off("public_pause", onPause);
      socket.off("public_next_fight", onNextFight);
    };
  }, [competitionId, tapisId]);

  const computedResult = useMemo(() => {
    if (!combatData) return null;

    try {
      return calculateJJCombatResult(
        combatData.redScore || createEmptyJJScore(),
        combatData.blueScore || createEmptyJJScore(),
      );
    } catch (error) {
      console.error("Erreur calcul résultat JJ Combat public :", error);
      return null;
    }
  }, [combatData]);

  const derivedWinnerColor = useMemo(() => {
    if (!computedResult) return null;

    if (computedResult.result1 === "V" && computedResult.result2 === "D") {
      return "red";
    }

    if (computedResult.result1 === "D" && computedResult.result2 === "V") {
      return "blue";
    }

    return null;
  }, [computedResult]);

  const winnerColor = combatData?.winnerColor || derivedWinnerColor || null;

  const showExtraPeriodBanner = useMemo(() => {
    if (!combatData) return false;

    const tiedAfterTime =
      combatData?.needExtraPeriod || computedResult?.needExtraPeriod;

    return Boolean(tiedAfterTime && safeNumber(combatData?.chrono, 0) <= 0);
  }, [combatData, computedResult]);

  const displayMeta = useMemo(() => {
    if (!combatData) return { ageLabel: "", weightLabel: "" };
    return buildDisplayMeta(combatData);
  }, [combatData]);

  const chronoPercent = useMemo(() => {
    if (!combatData?.chronoMax) return 0;

    return Math.max(
      0,
      Math.min(100, (combatData.chrono / combatData.chronoMax) * 100),
    );
  }, [combatData]);

  const chronoColorClass = useMemo(() => {
    if (!combatData) return "text-white";
    if (showExtraPeriodBanner) return "text-yellow-300";
    if (combatData.chrono <= 10) return "text-red-400";
    if (combatData.chrono <= 30) return "text-amber-300";
    return "text-white";
  }, [combatData, showExtraPeriodBanner]);

  const nextRed = getFightCompetitor(nextFight, 1);
  const nextBlue = getFightCompetitor(nextFight, 2);

  if (isPaused) {
    return (
      <WaitingScreen
        competitionName={competitionName}
        tapisId={tapisId}
        title="Tapis en pause"
        subtitle={nextMsg}
        nextFight={nextFight}
      />
    );
  }

  if (!combatData) {
    return (
      <WaitingScreen
        competitionName={competitionName}
        tapisId={tapisId}
        title="En attente du combat..."
        subtitle={nextMsg}
        nextFight={nextFight}
      />
    );
  }

  return (
    <div className="w-screen h-screen overflow-hidden bg-black text-white font-black flex flex-col">
      {/* Bandeau haut */}
      <div className="h-[11%] bg-black px-8 md:px-10 flex items-center justify-between border-b border-white/10">
        <div className="min-w-0">
          <div className="truncate text-[clamp(1.8rem,3.6vw,3.8rem)] font-black leading-none">
            {competitionName || "Compétition"}
          </div>
          <div className="mt-2 text-[clamp(1rem,1.8vw,1.45rem)] text-white/65 font-semibold uppercase tracking-[0.22em]">
            {combatData.discipline || "Ju-Jitsu Combat"}
          </div>
        </div>

        <div className="text-right min-w-0">
          {!!nextFight && (
            <div className="truncate text-[clamp(1rem,1.7vw,1.4rem)] text-yellow-300 font-bold">
              Prochain : {getCompetiteurDisplayName(nextRed)} vs{" "}
              {getCompetiteurDisplayName(nextBlue)}
            </div>
          )}
          <div className="mt-1 text-[clamp(2.2rem,4.2vw,4.8rem)] leading-none">
            Tapis {tapisId}
          </div>
        </div>
      </div>

      {/* Ligne Rouge */}
      <div className="h-[30%] text-[110%]">
        <JJCombatPublicRow
          color="red"
          competiteur={combatData.competiteur1}
          score={combatData.redScore}
          osaeSeconds={
            combatData.osaeFighter === "red" ? combatData.osaeSeconds : 0
          }
          isOsaeActive={combatData.osaeFighter === "red"}
          highlightWinner={winnerColor === "red"}
        />
      </div>

      {/* Ligne Bleue */}
      <div className="h-[30%] border-t border-white/10 text-[110%]">
        <JJCombatPublicRow
          color="blue"
          competiteur={combatData.competiteur2}
          score={combatData.blueScore}
          osaeSeconds={
            combatData.osaeFighter === "blue" ? combatData.osaeSeconds : 0
          }
          isOsaeActive={combatData.osaeFighter === "blue"}
          highlightWinner={winnerColor === "blue"}
        />
      </div>

      {/* Bas */}
      <div className="h-[29%] bg-black border-t border-white/10 px-8 md:px-10 py-5 grid grid-cols-3 gap-8 items-center">
        <div className="min-w-0">
          <div className="text-[clamp(2.4rem,4.8vw,5.2rem)] uppercase leading-none">
            {displayMeta.ageLabel || "Catégorie"}
          </div>

          {!!displayMeta.weightLabel && (
            <div className="mt-4 text-[clamp(1.9rem,3vw,3.8rem)] uppercase text-white/90 leading-none">
              {displayMeta.weightLabel}
            </div>
          )}
        </div>

        <div className="text-center px-2">
          <div className="flex items-start justify-center gap-5">
            {Object.entries(PART_CONFIG).map(([partNumber, cfg]) => {
              const active = Number(partNumber) === combatData.currentPart;

              return (
                <div key={partNumber} className="flex flex-col items-center w-[180px]">
                  <div
                    className={[
                      "w-[94px] h-[94px] rounded-2xl border-2 flex items-center justify-center",
                      "text-[clamp(2rem,4vw,3.6rem)] leading-none shadow-lg",
                      active
                        ? "bg-orange-500 border-orange-400 text-white"
                        : "bg-white border-white text-black",
                    ].join(" ")}
                  >
                    {partNumber}
                  </div>

                  <div
                    className={[
                      "mt-3 text-center font-semibold leading-tight",
                      "text-[clamp(.9rem,1.25vw,1.2rem)]",
                      active ? "text-white" : "text-white/72",
                    ].join(" ")}
                  >
                    {cfg.description}
                  </div>
                </div>
              );
            })}
          </div>

          {showExtraPeriodBanner ? (
            <div className="mt-5 inline-flex items-center rounded-full bg-yellow-400/15 border border-yellow-300/30 px-7 py-3 text-yellow-300 text-[clamp(1.05rem,1.8vw,1.5rem)]">
              HIKIWAKE • période supplémentaire
            </div>
          ) : (
            !!(combatData.message || nextMsg) && (
              <div className="mt-5 text-[clamp(1rem,1.65vw,1.35rem)] text-white/78 font-semibold">
                {combatData.message || nextMsg}
              </div>
            )
          )}
        </div>

        <div className="flex flex-col items-end justify-center">
          <div
            className={`tabular-nums leading-none text-[clamp(4.8rem,11vw,9.2rem)] ${chronoColorClass}`}
          >
            {formatChrono(combatData.chrono)}
          </div>

          <div className="mt-5 w-full max-w-[560px] h-[22px] rounded-full overflow-hidden bg-white/10">
            <div
              className="h-full bg-emerald-400 transition-all duration-300"
              style={{ width: `${chronoPercent}%` }}
            />
          </div>

          <div className="mt-3 text-[clamp(.95rem,1.45vw,1.2rem)] text-white/58 font-semibold uppercase tracking-[0.18em]">
            Temps réglementaire : {formatChrono(combatData.chronoMax)}
          </div>
        </div>
      </div>
    </div>
  );
}